import type { Root } from "../../../../../supabase/functions/sync-control/model";
import { publicJwk } from "../../../../../supabase/functions/sync-control/policy";
import type { LocalStoreClient } from "../localStore/client";
import type { SyncControlClient } from "./controlClient";
import {
  base64url,
  canonical,
  newDataKey,
  openDeviceEnvelope,
  sealDeviceEnvelope,
  unwrapDataKeyBytes,
} from "./cryptoV2";
import type { DeviceVault, UnlockedDevice } from "./deviceVault";
import { dataKeyScope } from "./syncScopes";

export async function activateDeviceSetup(
  api: SyncControlClient,
  vault: DeviceVault,
  client: LocalStoreClient,
  workspaceId: string,
  keys: Pick<UnlockedDevice, "wrapping" | "signing" | "agreement">,
) {
  const accountId = api.device.accountId;
  const local = await client.syncState(workspaceId);
  let pending = await vault.pendingSetup(accountId);
  const existing = await api.accountState();
  if (!pending) {
    if (local || existing.root) throw new Error("sync-root-exists");
    pending = await vault.prepareSetup(
      { accountId, deviceId: api.device.deviceId },
      dataKeyScope({ datasetId: crypto.randomUUID(), generation: crypto.randomUUID(), keyVersion: 1 }),
      newDataKey(),
      keys,
    );
  }
  if (pending.deviceId !== api.device.deviceId) throw new Error("sync-device-scope-mismatch");
  const publicDevice = await api.publicDevice();
  if (
    canonical(publicJwk(await crypto.subtle.exportKey("jwk", pending.signing.publicKey))) !==
    canonical(publicDevice.signing)
  )
    throw new Error("sync-device-scope-mismatch");
  const bytes = await unwrapDataKeyBytes(pending.wrapping, pending.scope, pending.wrapped);
  try {
    const material = base64url(bytes);
    let root: Root;
    if (existing.root) {
      root = existing.root;
      if (
        canonical(dataKeyScope(root)) !== canonical(pending.scope) ||
        root.recoveryMode !== "device" ||
        (!local && root.checkpoint)
      )
        throw new Error("sync-existing-data-confirmation-required");
      const envelope = root.deviceEnvelopes[pending.deviceId];
      if (!envelope) throw new Error("sync-device-revoked");
      const read = (await openDeviceEnvelope(
        pending.agreement.privateKey,
        { ...pending.scope, objectId: pending.deviceId },
        envelope as Awaited<ReturnType<typeof sealDeviceEnvelope>>,
      )) as { dataKey?: string };
      if (read.dataKey !== material) throw new Error("sync-key-scope");
    } else {
      if (local) throw new Error("sync-generation-mismatch");
      const value = {
        datasetId: pending.scope.datasetId,
        generation: pending.scope.generation,
        keyVersion: pending.scope.keyVersion,
        recoveryMode: "device" as const,
        recoveryPublicKey: publicDevice.signing,
        deviceEnvelopes: {
          [pending.deviceId]: await sealDeviceEnvelope(
            publicDevice.agreement,
            { ...pending.scope, objectId: pending.deviceId },
            { dataKey: material },
          ),
        },
      };
      root = (await api.request<{ root: Root }>("setup-device", { device: publicDevice, root: value })).root;
    }
    if (canonical(dataKeyScope(root)) !== canonical(pending.scope)) throw new Error("sync-generation-mismatch");
    const attachment = {
      datasetId: pending.scope.datasetId,
      generation: pending.scope.generation,
      keyVersion: pending.scope.keyVersion,
      deviceId: pending.deviceId,
    };
    if (local) {
      if (
        local.datasetId !== attachment.datasetId ||
        local.generation !== attachment.generation ||
        local.deviceId !== attachment.deviceId ||
        local.keyVersion !== attachment.keyVersion
      )
        throw new Error("sync-generation-mismatch");
      await client.syncUnlock(workspaceId, attachment, pending.dataKey);
    } else {
      // This captures the existing workspace in its normal SQL transaction;
      // it never installs an empty test snapshot or replaces local records.
      await client.syncCreateLocal(workspaceId, attachment, pending.dataKey);
    }
    await vault.finishSetup(accountId);
    return pending;
  } finally {
    bytes.fill(0);
  }
}
